import {
  Button,
  Card,
  Flex,
  Grid,
  View,
  Text,
  useBreakpointValue,
} from "@aws-amplify/ui-react";
import React, { useMemo, useState } from "react";
import { Lobby } from "../../game/Types/LobbyTypes";
import { ImageComponent, RaisedButton } from "../../../components";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import getTimeSince from "../../../util/timeSinceUpdated";
import { useNavigate } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { FaEye } from "react-icons/fa6";
import useConfirmationModal from "../../../hooks/useConfirmationModal";
import RestAPI from "../../../util/RestAPI";
import { useHistoryContext } from "../hooks/useHistoryContext";
import { getTeamPlayers } from "../../game/services/getTeamPlayers";
import StarIcon from "../../../components/icons/StarIcon";
import { Team } from "../../game/Types/TeamTypes";

type Props = {
  lobby: Lobby;
};

export default function GameCard({ lobby }: Props) {
  const [isDeleted, setIsDeleted] = useState(false);
  const queryClient = useQueryClient();
  const quiz = useMemo(() => {
    return lobby.quiz;
  }, [lobby.quiz]);

  const { teamsMap } = useHistoryContext();
  const teams: Team[] | undefined = teamsMap[lobby.id];

  const answerResults = useMemo(() => {
    return teams?.flatMap((team) => team.gameData.answerResults) || [];
  }, [teams]);

  const noResults = useMemo(() => {
    return answerResults.length === 0;
  }, [answerResults]);

  const size = useBreakpointValue({
    small: "small",
    medium: "medium",
  }) as "small" | "medium";

  const navigate = useNavigate();

  const playerQueries = useQueries({
    queries:
      teams?.map((team) => ({
        queryKey: ["lobbies", lobby.id, "teams", team.id, "players"],
        queryFn: async () => {
          return getTeamPlayers(team.id);
        },
      })) ?? [],
  });

  const players = useMemo(() => {
    return playerQueries
      .map((query) => query.data)
      .flat()
      .filter((p) => p !== undefined);
  }, [playerQueries]);

  const [DeleteModal, setShowModal] = useConfirmationModal({
    headerMessage: "Delete Game Results?",
    bodyMessage: "This action is irreversible. All data will be deleted.",
    confirmFn: async () => {
      setIsDeleted(true);
      await RestAPI.del(`/lobbies/object/${lobby.pin}/${lobby.id}`).then(
        async () => {
          queryClient.invalidateQueries({ queryKey: ["lobbies"] });
        }
      );
    },
  });

  const participantCountMessage = useMemo(() => {
    return `${players.length} Participant${players.length === 1 ? "" : "s"}`;
  }, [players]);

  return (
    <>
      <DeleteModal />
      <Card
        id="raised-item"
        padding={"0"}
        width={"100%"}
        opacity={isDeleted ? 0.3 : 1}
      >
        <Grid
          padding={"0"}
          templateColumns={{
            base: "1fr",
            small: "1fr 1fr",
            medium: "4fr 10fr 2fr",
          }}
          templateRows={{
            base: "minmax(0,1fr)",
            small: "minmax(0,1fr)",
            medium: "160px",
          }}
        >
          <Flex padding={"0"}>
            <ImageComponent
              canEdit={false}
              image={quiz.image}
              autoPickPrompt={""}
              width={{ base: "100%", medium: "300px" }}
              maxHeight={"200px"}
              objectFit="cover"
              alt="Quiz Image"
              borderRadius={{
                base: "large large 0 0",
                small: "large 0 large 0",
                medium: "large 0 0 large",
              }}
            ></ImageComponent>
          </Flex>

          <Grid
            padding={"small"}
            // width={{ base: '100%', medium: '50%' }}
            templateColumns={{ base: "100%", medium: "1fr 1fr" }}
            rowGap={"small"}
            columnGap={"medium"}
          >
            <Flex direction={"column"} gap={0} justifyContent={"space-between"}>
              <Text id="sectionTitle">{quiz.title}</Text>
              <View>
                <Text fontSize={"smaller"}>
                  Played {getTimeSince(new Date(lobby.createdAt))} ago
                </Text>
              </View>
            </Flex>

            <Flex direction={"column"} fontFamily={"Poppins"}>
              <Text fontWeight={"bold"} fontSize={"small"}>
                <StarIcon /> {participantCountMessage}
              </Text>
            </Flex>
          </Grid>

          <Flex
            padding={"small"}
            direction={{ base: "row-reverse", medium: "column" }}
            columnSpan={{ base: 1, small: 2, medium: 1 }}
          >
            <Flex justifyContent={"right"} gap="small">
              {/* <Button
              variation="link"
              id="raised-btn"
              onClick={() => setShowSettings(true)}
            >
              <SettingsIcon />
            </Button> */}
              <Button
                variation="link"
                id="raised-btn"
                onClick={() => {
                  setShowModal(true);
                }}
              >
                <DeleteIcon />
              </Button>
            </Flex>

            <RaisedButton
              isDisabled={noResults}
              variation="primary"
              aria-label="View results"
              backgroundColor={noResults ? "background.disabled" : "purple.90"}
              textAlign={"center"}
              size="small"
              onClick={() => navigate(lobby.id)}
            >
              {size === "small" ? (
                <FaEye />
              ) : noResults ? (
                "No Results"
              ) : (
                "View Results"
              )}
            </RaisedButton>
          </Flex>
        </Grid>
      </Card>
    </>
  );
}
